/**
 * Attachment manager for handling file selections and previews
 */
export class AttachmentManager {
	/**
	 * @param {Object} options
	 * @param {string} options.btnId
	 * @param {string} options.inputId
	 * @param {string} options.previewContainerId
	 * @param {string} options.wrapperId
	 * @param {string} [options.templateId] - Optional, defaults to 'file-item-template'
	 */
	constructor(options) {
		this.btn = document.getElementById(options.btnId);
		this.input = document.getElementById(options.inputId);
		this.previewContainer = document.getElementById(options.previewContainerId);
		this.wrapper = document.getElementById(options.wrapperId);
		this.template = document.getElementById(options.templateId || 'file-item-template');

		this.selectedFiles = [];
		this.init();
	}

	init() {
		if (!this.btn || !this.input || !this.previewContainer) return;

		// Trigger file input
		this.btn.onclick = () => {
			this.input.click();
		};

		// Handle file selection
		this.input.onchange = (e) => {
			const files = Array.from(e.target.files);
			this.handleFiles(files);
			this.input.value = '';
		};
	}

	handleFiles(files) {
		files.forEach(file => {
			const exists = this.selectedFiles.some(f => f.name === file.name && f.size === file.size);
			if (exists) return;

			this.selectedFiles.push(file);
			this.renderPreview(file);
		});
	}

	renderPreview(file) {
		if (!this.wrapper || !this.template) {
			// Fallback: simple element creation if template not found
			this.renderSimplePreview(file);
			return;
		}

		this.wrapper.classList.remove('hidden');
		if (!this.wrapper.classList.contains('flex')) {
			this.wrapper.classList.add('flex');
		}
		if (!this.wrapper.classList.contains('flex-col')) {
			this.wrapper.classList.add('flex-col');
		}

		const clone = this.template.content.cloneNode(true);
		const previewItem = clone.querySelector('div');

		const nameEl = previewItem.querySelector('.file-name');
		if (nameEl) nameEl.textContent = file.name;

		const removeBtn = previewItem.querySelector('.remove-btn');
		if (removeBtn) {
			removeBtn.onclick = (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.removeFile(file, previewItem);
			};
		}

		this.previewContainer.appendChild(previewItem);
	}

	renderSimplePreview(file) {
		const item = document.createElement('div');
		item.className = 'flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-100 mb-1';
		item.innerHTML = `
			<div class="flex items-center gap-2 truncate">
				<span class="fi fi-rr-file text-primary-500 text-base"></span>
				<span class="text-xs font-medium text-gray-700 truncate">${file.name}</span>
			</div>
			<button class="text-gray-400 hover:text-red-500 remove-btn w-fit">
				<span class="fi fi-rr-cross-small text-base"></span>
			</button>
		`;

		const removeBtn = item.querySelector('.remove-btn');
		removeBtn.onclick = (e) => {
			e.stopPropagation();
			this.removeFile(file, item);
		};

		this.previewContainer.appendChild(item);
		if (this.wrapper) {
			this.wrapper.classList.remove('hidden');
			if (!this.wrapper.classList.contains('flex')) {
				this.wrapper.classList.add('flex');
			}
			if (!this.wrapper.classList.contains('flex-col')) {
				this.wrapper.classList.add('flex-col');
			}
		}
	}

	removeFile(file, element) {
		this.selectedFiles = this.selectedFiles.filter(f => f !== file);
		element.remove();

		if (this.selectedFiles.length === 0 && this.wrapper) {
			this.wrapper.classList.add('hidden');
			this.wrapper.classList.remove('flex');
		}
	}

	getSelectedFiles() {
		return this.selectedFiles;
	}

	clear() {
		this.selectedFiles = [];
		if (this.previewContainer) this.previewContainer.innerHTML = '';
		if (this.wrapper) {
			this.wrapper.classList.add('hidden');
			this.wrapper.classList.remove('flex');
		}
	}
}

// Keep backward compatibility for existing singleton usage
let studentAttachment = null;
export function initFileAttachment() {
	studentAttachment = new AttachmentManager({
		btnId: 'btn-attach-file',
		inputId: 'question-file-input',
		previewContainerId: 'file-preview-container',
		wrapperId: 'attachment-wrapper'
	});
}

export function getSelectedFiles() {
	return studentAttachment ? studentAttachment.getSelectedFiles() : [];
}

export function clearFiles() {
	if (studentAttachment) studentAttachment.clear();
}
